import { useEffect, useMemo, useRef } from 'react';
import { formatDistanceToNow, format, isToday, isYesterday } from 'date-fns';

function getTimelineItems(timeline) {
  if (timeline?.pages) {
    return timeline.pages.flatMap((page) => {
      if (Array.isArray(page)) return page;
      if (Array.isArray(page?.results)) return page.results;
      return [];
    });
  }
  if (Array.isArray(timeline)) return timeline;
  if (Array.isArray(timeline?.results)) return timeline.results;
  return [];
}

function formatTimeAgo(dateString, { alwaysRelative = false } = {}) {
  if (!dateString) return '';
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;

    if (alwaysRelative || isToday(d) || isYesterday(d)) {
      return formatDistanceToNow(d, { addSuffix: true });
    }

    return format(d, 'dd/MM/yyyy');
  } catch {
    return dateString;
  }
}

function TimelineSpinner({ isPage, size = 'md' }) {
  const sizeClass = size === 'sm' ? 'w-5 h-5' : 'w-6 h-6';
  const colorClass = isPage
    ? 'border-[#50AB3D]/30 border-t-[#50AB3D]'
    : 'border-primary/30 border-t-primary';

  return (
    <div
      className={`${sizeClass} border-2 rounded-full animate-spin ${colorClass}`}
    />
  );
}

function PageTimelineItem({ item, isLast }) {
  const actorName = item.actor_name || 'System';
  const initial = actorName.charAt(0).toUpperCase();
  const timeAgo = formatTimeAgo(
    item.occurred_at || item.created_at || item.timestamp,
    { alwaysRelative: true },
  );
  const description = item.description || 'performed an activity.';

  return (
    <div className={`relative flex gap-4 ${isLast ? '' : 'pb-4'}`}>
      {/* Avatar rail + connector */}
      <div className="relative w-10 shrink-0 self-stretch">
        <div className="relative z-10 w-10 h-10 rounded-full border-[1.5px] border-[#50AB3D] bg-white overflow-hidden flex items-center justify-center text-sm font-bold text-[#50AB3D]">
          {item.actor_avatar ? (
            <img
              src={item.actor_avatar}
              alt={actorName}
              className="w-full h-full object-cover"
            />
          ) : (
            initial
          )}
        </div>
        {!isLast && (
          <div className="absolute top-10 -bottom-4 left-1/2 -translate-x-1/2 w-[2px] bg-[#50AB3D]" />
        )}
      </div>

      {/* Name + description | timestamp */}
      <div className="flex-1 min-w-0 flex items-start justify-between gap-6 pt-1">
        <div className="min-w-0">
          <p className="text-[15px] font-semibold text-[#1F2241] leading-tight">
            {actorName}
          </p>
          <p className="mt-1 text-sm text-[#868686] leading-relaxed">
            {description}
          </p>
        </div>
        <span className="text-sm text-[#868686] shrink-0 whitespace-nowrap leading-tight pt-0.5">
          {timeAgo}
        </span>
      </div>
    </div>
  );
}

function DrawerTimelineItem({ item }) {
  const actorName = item.actor_name || 'System';
  const initial = actorName.charAt(0).toUpperCase();
  const timeAgo = formatTimeAgo(
    item.occurred_at || item.created_at || item.timestamp,
  );

  return (
    <div className="relative flex items-start gap-3">
      <div className="w-8 h-8 rounded-full shadow-sm flex items-center justify-center shrink-0 text-sm font-bold z-10 absolute left-0 mt-0.5 bg-layer-3-elevated border border-border-default text-text-secondary">
        {item.actor_avatar ? (
          <img
            src={item.actor_avatar}
            alt={actorName}
            className="w-full h-full rounded-full object-cover"
          />
        ) : (
          initial
        )}
      </div>

      <div className="flex-1 pl-[3.25rem] pt-1">
        <div className="flex items-start justify-between gap-4">
          <p className="text-sm leading-relaxed text-text-secondary">
            <span className="font-semibold text-text-primary">{actorName}</span>{' '}
            <span>{item.description || 'performed an activity.'}</span>
          </p>
          <span className="text-[11px] font-medium shrink-0 whitespace-nowrap mt-1 text-text-muted">
            {timeAgo}
          </span>
        </div>
      </div>
    </div>
  );
}

export function CRMActivityTimeline({
  timeline,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  variant = 'drawer',
}) {
  const scrollContainerRef = useRef(null);
  const observerTarget = useRef(null);
  const isPage = variant === 'page';

  useEffect(() => {
    if (!hasNextPage || !fetchNextPage) return;

    const rootElement = isPage ? scrollContainerRef.current : null;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0]?.isIntersecting &&
          hasNextPage &&
          !isFetchingNextPage &&
          fetchNextPage
        ) {
          fetchNextPage();
        }
      },
      {
        root: rootElement,
        rootMargin: '100px',
        threshold: 0.1,
      },
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
      observer.disconnect();
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, isPage]);

  const handleScroll = (e) => {
    if (!hasNextPage || isFetchingNextPage || !fetchNextPage) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 80) {
      fetchNextPage();
    }
  };

  const timelineData = getTimelineItems(timeline);

  const sortedItems = useMemo(() => {
    const sorted = [...timelineData].sort((a, b) => {
      const aTime = new Date(
        a.occurred_at || a.created_at || a.timestamp || 0,
      ).getTime();
      const bTime = new Date(
        b.occurred_at || b.created_at || b.timestamp || 0,
      ).getTime();
      return bTime - aTime;
    });

    const seen = new Set();
    return sorted.filter((item) => {
      const key =
        item.id != null
          ? `id_${item.id}`
          : `${item.occurred_at || item.created_at || item.timestamp}_${item.description || ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [timelineData]);

  return (
    <div className={isPage ? '' : 'mt-6 pt-6 border-t border-border-default'}>
      {!isPage && (
        <h4 className="text-base font-bold text-text-primary mb-6">Timeline</h4>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center py-6">
          <TimelineSpinner isPage={isPage} />
        </div>
      ) : sortedItems.length > 0 ? (
        isPage ? (
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="max-h-[320px] overflow-y-auto custom-scrollbar pr-1"
          >
            {sortedItems.map((item, idx) => (
              <PageTimelineItem
                key={item.id || idx}
                item={item}
                isLast={!hasNextPage && idx === sortedItems.length - 1}
              />
            ))}
            {hasNextPage && (
              <div
                ref={observerTarget}
                className="h-9 w-full flex justify-center items-center py-2 shrink-0"
              >
                {isFetchingNextPage && (
                  <TimelineSpinner isPage={isPage} size="sm" />
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 relative before:absolute before:inset-0 before:ml-4 before:-translate-x-px before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-border-default before:to-transparent">
            {sortedItems.map((item, idx) => (
              <DrawerTimelineItem key={item.id || idx} item={item} />
            ))}
            {hasNextPage && (
              <div
                ref={observerTarget}
                className="h-9 w-full flex justify-center items-center py-2 shrink-0"
              >
                {isFetchingNextPage && (
                  <TimelineSpinner isPage={isPage} size="sm" />
                )}
              </div>
            )}
          </div>
        )
      ) : (
        <p
          className={`text-sm ${isPage ? 'text-[#868686]' : 'text-text-muted'}`}
        >
          No timeline activity found.
        </p>
      )}
    </div>
  );
}
